import React, { useEffect, useState } from 'react';
import { AlertTriangle, TrendingUp, Clock, BarChart2, Loader2, Users, Target, CheckCircle2 } from 'lucide-react';
import api from '../../api/client';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

interface TimePrediction {
  vacancyId: string;
  title: string;
  predictedDays: number;
  candidatesCount: number;
  avgSalary: number;
}

interface OfferRisk {
  candidateId: string;
  name: string;
  riskPercentage: number;
  daysOnOffer: number;
  source: string;
}

interface FunnelForecast {
  predictedNextMonth: {
    total: number;
    interviews: number;
    offers: number;
    hired: number;
  } | null;
  historicalMonthlyData: { _id: string; total: number; interviews: number; offers: number; hired: number }[];
  note?: string;
}

const PredictiveDashboard: React.FC = () => {
  const [timePredictions, setTimePredictions] = useState<TimePrediction[]>([]);
  const [offerRisks, setOfferRisks] = useState<OfferRisk[]>([]);
  const [funnelForecast, setFunnelForecast] = useState<FunnelForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [timeRes, riskRes, funnelRes] = await Promise.all([
          api.get('/predictive-analytics/time-to-fill'),
          api.get('/predictive-analytics/offer-risk'),
          api.get('/predictive-analytics/funnel-forecast')
        ]);
        setTimePredictions(timeRes.data.predictions || []);
        setOfferRisks(riskRes.data.candidates || []);
        setFunnelForecast(funnelRes.data);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки прогнозов:', err);
        setError('Не удалось загрузить прогнозные данные.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#64748b' }}>
        <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', marginRight: 12 }} />
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        Загрузка прогнозов...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.glassCard, margin: '40px auto', maxWidth: 600, textAlign: 'center', padding: 60 }}>
        <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: 16 }} />
        <h2 style={{ color: '#1a1a2e', marginBottom: 8 }}>{error}</h2>
        <p style={{ color: '#64748b' }}>Попробуйте обновить страницу позже.</p>
      </div>
    );
  }

  // Расчет KPI
  const totalPredicted = timePredictions.length;
  const avgPredictedDays = totalPredicted > 0
    ? Math.round(timePredictions.reduce((sum, p) => sum + p.predictedDays, 0) / totalPredicted)
    : 0;
  const highRiskCount = offerRisks.filter(r => r.riskPercentage > 50).length;
  const predictedHires = funnelForecast?.predictedNextMonth?.hired || 0;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
          <TrendingUp size={24} />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Прогнозная аналитика</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Предиктивные модели найма и риски</p>
        </div>
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><BarChart2 size={22} /></div>
          <div><div style={styles.kpiLabel}>Вакансий в прогнозе</div><div style={styles.kpiValue}>{totalPredicted}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(106,17,203,0.1)', color: '#6a11cb' }}><Clock size={22} /></div>
          <div><div style={styles.kpiLabel}>Ср. время закрытия</div><div style={styles.kpiValue}>{avgPredictedDays} дн.</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}><AlertTriangle size={22} /></div>
          <div><div style={styles.kpiLabel}>Высокий риск оффера</div><div style={styles.kpiValue}>{highRiskCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><Target size={22} /></div>
          <div><div style={styles.kpiLabel}>Прогноз наймов (мес.)</div><div style={styles.kpiValue}>{predictedHires}</div></div>
        </div>
      </div>

      {/* Прогноз воронки (График) */}
      <div style={{ ...styles.glassCard, marginBottom: 24 }}>
        <h3 style={styles.sectionTitle}><TrendingUp size={18} /> Прогноз воронки на следующий месяц</h3>
        {!funnelForecast?.predictedNextMonth ? (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Недостаточно данных для прогноза</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={styles.metricBox}>
                <span style={styles.metricLabel}>Всего заявок</span>
                <strong style={styles.metricValue}>{funnelForecast.predictedNextMonth.total}</strong>
              </div>
              <div style={styles.metricBox}>
                <span style={styles.metricLabel}>Интервью</span>
                <strong style={styles.metricValue}>{funnelForecast.predictedNextMonth.interviews}</strong>
              </div>
              <div style={styles.metricBox}>
                <span style={styles.metricLabel}>Офферов</span>
                <strong style={styles.metricValue}>{funnelForecast.predictedNextMonth.offers}</strong>
              </div>
              <div style={styles.metricBox}>
                <span style={styles.metricLabel}>Наймов</span>
                <strong style={{ ...styles.metricValue, color: '#10b981' }}>{funnelForecast.predictedNextMonth.hired}</strong>
              </div>
            </div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={funnelForecast.historicalMonthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                  <XAxis dataKey="_id" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }} />
                  <Legend wrapperStyle={{ fontSize: 13, paddingTop: 10 }} />
                  <Line type="monotone" dataKey="total" stroke="#2575fc" name="Всего" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="interviews" stroke="#6a11cb" name="Интервью" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="offers" stroke="#f59e0b" name="Офферы" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="hired" stroke="#10b981" name="Наймы" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* Две таблицы в ряд */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        
        {/* Прогноз времени закрытия */}
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><Clock size={18} /> Время закрытия вакансий</h3>
          {timePredictions.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Нет открытых вакансий для прогноза</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Вакансия</th>
                    <th style={styles.th}>Кандидатов</th>
                    <th style={styles.th}>Зарплата</th>
                    <th style={styles.th}>Прогноз</th>
                  </tr>
                </thead>
                <tbody>
                  {timePredictions.map(p => (
                    <tr key={p.vacancyId} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                      <td style={styles.td}>{p.title}</td>
                      <td style={styles.td}>{p.candidatesCount}</td>
                      <td style={styles.td}>{p.avgSalary ? `${p.avgSalary.toLocaleString()} ₽` : '—'}</td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 12, fontWeight: 700, fontSize: 13,
                          background: p.predictedDays > 45 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                          color: p.predictedDays > 45 ? '#ef4444' : '#10b981'
                        }}>
                          {p.predictedDays} дн.
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Риск отказа от оффера */}
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}><AlertTriangle size={18} /> Риск отказа от оффера</h3>
          {offerRisks.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Нет кандидатов на этапе оффера</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Кандидат</th>
                    <th style={styles.th}>Дней на оффере</th>
                    <th style={styles.th}>Источник</th>
                    <th style={styles.th}>Риск</th>
                  </tr>
                </thead>
                <tbody>
                  {offerRisks.map(r => (
                    <tr key={r.candidateId} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                      <td style={styles.td}>{r.name}</td>
                      <td style={styles.td}>{r.daysOnOffer}</td>
                      <td style={styles.td}>{r.source}</td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 12, fontWeight: 700, fontSize: 13,
                          background: r.riskPercentage > 50 ? 'rgba(239,68,68,0.1)' : r.riskPercentage > 30 ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
                          color: r.riskPercentage > 50 ? '#ef4444' : r.riskPercentage > 30 ? '#f59e0b' : '#10b981'
                        }}>
                          {r.riskPercentage}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: 8 },
  metricBox: { background: 'rgba(255,255,255,0.4)', borderRadius: 16, padding: '16px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid rgba(255,255,255,0.5)' },
  metricLabel: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' },
  metricValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '12px 12px', fontSize: 13, color: '#1a1a2e', verticalAlign: 'middle' },
};

export default PredictiveDashboard;
